import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, AlertTriangle, X, RotateCcw } from "lucide-react";

/**
 * Modal para mover contrato para Recuperação OU retornar para Carteira Normal.
 *
 * Props:
 *   • mode: "enter" | "return"
 *   • contract: contrato serializado (precisa de id + recovery_record_id se return)
 *   • onClose, onDone
 */
export default function RecoveryToggleModal({ mode, contract, recordType = "contract", onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(mode === "enter"); // enter não exige checkbox extra
  const [busy, setBusy] = useState(false);

  const isEnter = mode === "enter";
  const label = recordType === "installment_contract"
    ? (contract?.kind === "acordo" ? "Acordo" : "Parcelado")
    : "Contrato";

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) {
      toast.error("Informe o motivo (mínimo 5 caracteres)");
      return;
    }
    if (!confirmed) {
      toast.error("Confirme a operação marcando o aviso");
      return;
    }
    setBusy(true);
    try {
      if (isEnter) {
        await api.post("/recovery/enter", {
          record_type: recordType,
          record_id: contract.id,
          reason: reason.trim(),
        });
        toast.success(`${label} movido para Recuperação`);
      } else {
        await api.post(`/recovery/${contract.recovery_id}/return-to-normal`, {
          reason: reason.trim(),
        });
        toast.success(`${label} retornado para Carteira Normal`);
      }
      onDone && onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha na operação");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid={`recovery-${mode}-modal`}>
      <form onSubmit={submit} className="w-full max-w-lg rounded-lg border border-[#262626] bg-[#0d0d0d] shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
          <h3 className={`font-display text-base flex items-center gap-2 ${isEnter ? "text-amber-300" : "text-emerald-300"}`}>
            {isEnter ? <AlertTriangle size={16}/> : <RotateCcw size={16}/>}
            {isEnter ? "Mover para Recuperação" : "Retornar para Carteira Normal"}
          </h3>
          <button type="button" onClick={onClose} className="text-[#737373] hover:text-white" data-testid={`recovery-${mode}-close`}>
            <X size={18}/>
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className={`rounded-md border p-3 text-[12px] ${
            isEnter
              ? "border-amber-500/30 bg-amber-500/5 text-amber-200/90"
              : "border-emerald-500/30 bg-emerald-500/5 text-emerald-200/90"
          }`}>
            {isEnter ? (
              <>
                Este contrato passará a ser tratado como <strong>carteira de Recuperação</strong>.
                Ele sairá imediatamente da <strong>taxa de inadimplência</strong>, do <strong>valor total
                em atraso</strong> e de todos os <strong>indicadores da carteira operacional</strong> do
                dashboard principal. Cálculo de dias em atraso e pagamentos continuam normais.
                Reversível a qualquer momento.
              </>
            ) : (
              <>
                Este contrato voltará a ser contabilizado <strong>normalmente</strong> em todos os
                indicadores da carteira operacional (dashboard, taxa de inadimplência, valor em atraso).
                A operação será registrada com data, hora, usuário e motivo.
              </>
            )}
          </div>

          <div>
            <label className="fc-label">Motivo <span className="text-[#737373] font-normal">(obrigatório, mínimo 5 caracteres)</span></label>
            <textarea
              rows={3}
              className="fc-input resize-none"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder={isEnter
                ? "Ex: cliente não responde há 45 dias, iniciar cobrança judicial"
                : "Ex: cliente pagou parcela e voltou a responder"}
              required
              data-testid={`recovery-${mode}-reason`}
            />
          </div>

          {!isEnter && (
            <label className="flex items-start gap-2 text-[12px] text-[#A3A3A3] cursor-pointer" data-testid={`recovery-${mode}-confirm`}>
              <input
                type="checkbox"
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
                className="mt-0.5 accent-emerald-500"
              />
              <span>
                Confirmo que este contrato deve voltar para a carteira operacional e ser reincluído
                em todos os indicadores.
              </span>
            </label>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#1f1f1f] bg-[#0a0a0a]/50">
          <button type="button" onClick={onClose} className="fc-btn-ghost text-sm">Cancelar</button>
          <button
            type="submit"
            disabled={busy || !confirmed}
            className={`flex items-center gap-2 text-sm px-4 py-2 rounded ${
              isEnter ? "fc-btn-gold" : "bg-emerald-600/90 hover:bg-emerald-600 text-white disabled:opacity-40"
            }`}
            data-testid={`recovery-${mode}-submit`}
          >
            {busy && <Loader2 size={14} className="animate-spin"/>} {isEnter ? "Confirmar movimentação" : "Retornar ao operacional"}
          </button>
        </div>
      </form>
    </div>
  );
}
